import React, { useEffect } from "react";
import { useRouter } from "next/router";
import Head from "next/head";
import Link from "next/link";
import { fetchArticles } from "../../utils/article";
import {
  Avatar,
  Alert,
  Button,
  Chip,
  Typography } from "@material-tailwind/react";
import { 
  DocsNavbar,
  Footer
} from "@widgets";
import RightSidenav from "../../sections/right-side-menu";
import OfferBar from "widgets/offer-bar";

const fetchRecentPosts = (articles: any[]) => {
  const today = new Date();

  const sortedArticles = articles.sort((a, b) => {
    const dateA = new Date(
      a.frontMatter.created_at
        ? a.frontMatter.created_at.split('-').reverse().join('-')
        : today
    ) as any;
    const dateB = new Date(
      b.frontMatter.created_at
        ? b.frontMatter.created_at.split('-').reverse().join('-')
        : today
    ) as any;

    return dateB - dateA;
  });

  return sortedArticles;
};

const getTopTags = (articles) => {
  const tagCounts = {};

  articles.forEach((article) => {
    const tags = article.frontMatter.tag;

    if (tags) {
      tags.forEach((tag) => {
        if (tagCounts[tag]) {
          tagCounts[tag]++;
        } else {
          tagCounts[tag] = 1;
        }
      });
    }
  });

  const topTags = Object.keys(tagCounts).sort(
    (a, b) => tagCounts[b] - tagCounts[a]
  ).slice(0, 5);

  return topTags.map((tag) => ({
    tag,
    count: tagCounts[tag],
  }));
};

export async function getStaticProps() {
  const articles = fetchArticles();
  const recentPosts = fetchRecentPosts(articles);
  const topTags = getTopTags(articles);

  return {
    props: {
      articles,
      recentPosts,
      topTags
    },
  };
}

const BlogIndex = ({ recentPosts, topTags, slug }) => {
  const { asPath } = useRouter();
  const [mobileNav, setMobileNav] = React.useState(false);

  return (
    <>
      <Head>
        <title>Tailwind CSS Learning and News - Material Tailwind</title>
        <meta 
          name="title" 
          content="Tailwind CSS Learning and News - Material Tailwind" 
        />
        <meta 
          name="description" 
          content="Discover the latest updates and tips on Tailwind CSS at our blog. Catering to both beginners and experts with how-to articles and useful resources. Dive into the world of this popular open-source CSS framework!"   
        />
        <link
          rel="canonical"
          href="https://www.material-tailwind.com/blog"
        />
      </Head>
      <OfferBar />
      <DocsNavbar slug={slug} setMobileNav={setMobileNav} />
      <div className="relative mb-8 h-full w-full bg-white">
        <div className="px-8 lg:px-52">
          <div className="container mx-auto lg:flex">
            <div className="mt-6 w-full lg:w-[65%] lg:px-8">
              <Typography variant="h2" className="mb-2">
                Tailwind CSS Learning and News
              </Typography>
              <Typography variant="paragraph" className="text-gray-800 mb-8">
                Join our blog where we share the newest updates and learning tips about Tailwind CSS. Whether you&apos;re just starting out or you&apos;ve been using it for a while, there&apos;s something for everyone, from Tailwind css “how to use“ articles to Tailwind useful resources. So, come on in, have a look around, and enjoy the latest from the world of this popular open-source CSS framework!
              </Typography>
              <div>
                {recentPosts.map((article) => (
                  <div key={article.slug.join("/")} className="mb-12">
                    <a href={`/blog/${article.slug.join("/")}`}>
                      {article.frontMatter.articleThumbnail && (
                        <img
                          src={article.frontMatter.articleThumbnail}
                          alt={article.frontMatter.title}
                          className="rounded-lg shadow-xl mb-8"
                        />
                      )}
                      <Typography variant="h4">{article.frontMatter.title}</Typography>
                    </a>
                    <Typography variant="paragraph" className="mb-4">
                      {article.frontMatter.description}
                      <a
                        className="ml-2 text-blue-600 font-normal"
                        href={`/blog/${article.slug.join("/")}`}
                      >
                        Read More &rarr;
                      </a>
                    </Typography>
                    <div className="flex justify-between items-center pb-6 border-b">
                      <div className="flex items-center">
                        <Avatar
                          size="sm"
                          src={article.frontMatter.authorAvatar || "https://raw.githubusercontent.com/creativetimofficial/public-assets/master/blog/default-avatar.png"}
                          alt={article.frontMatter.authorName}
                          className="shadow"
                        />

                        <Typography variant="h6" className="ml-2">
                          {article.frontMatter.authorName}
                        </Typography>
                      </div>
                      <Typography variant="small" color="blue-gray" className="font-normal">
                        {article.frontMatter.updated_at && (
                          <>
                            Updated: {article.frontMatter.updated_at}
                          </>
                        )}
                      </Typography>
                    </div>
                  </div>
                ))}

              </div>
            </div>
            <div className="w-full lg:w-80 ml-auto">
              <RightSidenav recentPosts={recentPosts} topTags={topTags} />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BlogIndex;
